import {
    Component,
    OnInit,
    ChangeDetectorRef,
    signal,
    OnDestroy,
    ChangeDetectionStrategy,
    computed,
} from "@angular/core";
import { Subject } from "rxjs";
import { TypeManagerDecorator } from "../../../../main/type.map.service";
import { BudgetService } from "../budget.service";
import { Budget } from "../Budget";
import { BudgetLabelPhrases, BudgetLabels } from "../budget.labels";
import { Bind } from "../../../../decorators/bind.decorator";
import { S25ButtonComponent } from "../../../../standalone/s25.button.component";
import { S25LoadingInlineModule } from "../../../s25-loading/s25.loading.inline.module";
import { S25FadingSuccessMsgComponent } from "../../../s25-fading-success-msg/s25.fading.success.msg.component";
import { S25DropdownPaginatedModule } from "../../../s25-dropdown/s25.dropdown.paginated.module";
import { DropDownItem } from "../../../../pojo/DropDownItem";
import { BudgetAdminSummaryComponent } from "./budget.admin.summary.component";
import { BudgetAdminOrganizationsComponent } from "./budget.admin.organizations.component";
import { S25Util } from "../../../../util/s25-util";
import { SeSecurityStore } from "../../se.security.store";
import { inject } from "@angular/core";
import { OrganizationExportService } from "../../export/organization.export.service";

interface FiscalYearSummary {
    fiscalYear: string;
    totalOrganizations: number;
    totalBudgetsCreated: number;
    totalAllocated: number;
    pendingRequests: number;
}

@TypeManagerDecorator("s25-ng-budget-admin")
@Component({
    selector: "s25-ng-budget-admin",
    template: `
        @if (init()) {
            <div class="budget-admin-container">
                <!-- Header Section -->
                <div class="admin-header">
                    <h1>{{ labels.BUDGET_ADMINISTRATION }}</h1>
                    @if (hasFiscalYears()) {
                        <div class="fiscal-year-selector">
                            <label for="budget-admin-fiscal-year">Fiscal Year:</label>
                            <s25-ng-dropdown-search-criteria
                                type="fiscalYears"
                                [fieldId]="'budget-admin-fiscal-year'"
                                [(value)]="selectedFiscalYearItem"
                                placeholder="Select fiscal year"
                                ariaLabel="Fiscal year"
                                class="c-input fiscal-year-dropdown"
                                (valueChange)="onFiscalYearChange()"
                            />

                            <s25-ng-button
                                [type]="'outline'"
                                [buttonText]="'Refresh'"
                                [onClick]="loadAdminData"
                                [isLoading]="isLoading()"
                            ></s25-ng-button>
                            @if (securityStore.hasFLSOverride()) {
                                <s25-ng-button
                                    [type]="'outline'"
                                    [buttonText]="'Export'"
                                    [onClick]="exportFiscalYearBudgets"
                                    [isLoading]="exporting()"
                                    [disabled]="!selectedFiscalYear() || isLoading()"
                                ></s25-ng-button>
                            }
                        </div>
                    } @else {
                        <div class="fiscal-year-selector">
                            <p>No fiscal years found. Please add a fiscal year to continue.</p>
                        </div>
                    }
                </div>

                <!-- Loading State -->
                @if (isLoading()) {
                    <s25-ng-loading-inline-static
                        [text]="'Loading budget administration data...'"
                    ></s25-ng-loading-inline-static>
                }

                <!-- Error State -->
                @if (error) {
                    <div class="error-message">
                        <p>{{ error }}</p>
                        <s25-ng-button
                            [type]="'outline'"
                            [buttonText]="'Retry'"
                            [onClick]="loadAdminData"
                        ></s25-ng-button>
                    </div>
                }

                <!-- Success Message -->
                <s25-ng-fading-success-msg [(message)]="successMessage"></s25-ng-fading-success-msg>

                <!-- Main Content -->
                @if (!isLoading() && !error) {
                    <!-- Summary Cards -->
                    @if (securityStore.hasFLSOverride()) {
                        @if (fiscalYearSummary) {
                            <s25-ng-budget-admin-summary
                                [summary]="fiscalYearSummary"
                                [selectedFiscalYear]="selectedFiscalYear()"
                            >
                            </s25-ng-budget-admin-summary>
                        } @else {
                            <div class="summary-placeholder">
                                <p>Unable to load fiscal year summary data</p>
                            </div>
                        }
                    }

                    <!-- Organizations & Budgets View -->
                    <div class="organizations-view">
                        <s25-ng-budget-admin-organizations
                            [selectedFiscalYear]="selectedFiscalYear()"
                            (budgetChanged)="handleBudgetChanged()"
                        ></s25-ng-budget-admin-organizations>
                    </div>
                }
            </div>
        }
    `,
    styles: `
        .budget-admin-container {
            padding: 1.5rem;
            max-width: 1400px;
            margin: 0 auto;
        }

        .admin-header {
            display: flex;
            justify-content: space-between;
            align-items: center;
            margin-bottom: 2rem;
            padding-bottom: 1rem;
            border-bottom: 2px solid var(--color--border);
        }

        .admin-header h1 {
            margin: 0;
            color: var(--color--text);
            font-size: 2rem;
            font-weight: bold;
        }

        .fiscal-year-selector {
            display: flex;
            align-items: center;
            gap: 1rem;
        }

        .fiscal-year-selector label {
            font-weight: 600;
            color: var(--color--text-muted);
        }

        .fiscal-year-dropdown {
            min-width: 160px;
        }

        .error-message {
            text-align: center;
            padding: 2rem;
            background: var(--color--surface-bg-error);
            border: 1px solid color-mix(in srgb, var(--color--danger) 35%, var(--color--surface-bg));
            border-radius: 8px;
            margin: 1rem 0;
        }

        .error-message p {
            color: var(--color--danger);
            margin-bottom: 1rem;
        }

        .organizations-view {
            background: var(--color--surface-bg);
            border-radius: 8px;
            box-shadow: 0 2px 8px color-mix(in srgb, var(--color--text) 12%, transparent);
            overflow: hidden;
        }

        .summary-placeholder {
            text-align: center;
            padding: 2rem;
            background: color-mix(in srgb, var(--color--star-yellow) 22%, var(--color--surface-bg));
            border: 1px solid color-mix(in srgb, var(--color--star-yellow) 55%, var(--color--border));
            border-radius: 8px;
            margin-bottom: 2rem;
            color: var(--color--text-warning);
        }

        @media (max-width: 768px) {
            .budget-admin-container {
                padding: 1rem;
            }

            .admin-header {
                flex-direction: column;
                gap: 1rem;
                align-items: stretch;
            }

            .fiscal-year-selector {
                justify-content: center;
            }
        }
    `,
    standalone: true,
    imports: [
        S25LoadingInlineModule,
        S25ButtonComponent,
        S25FadingSuccessMsgComponent,
        BudgetAdminSummaryComponent,
        BudgetAdminOrganizationsComponent,
        S25DropdownPaginatedModule,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BudgetAdminComponent implements OnInit, OnDestroy {
    readonly labels = BudgetLabels;

    init = signal<boolean>(false);
    isLoading = signal<boolean>(false);
    exporting = signal<boolean>(false);
    error: string | null = null;
    successMessage = signal<string>("");

    securityStore = inject(SeSecurityStore);

    readonly hasFiscalYears = signal(false);
    readonly selectedFiscalYearItem = signal<DropDownItem<number> | null>(null);
    readonly selectedFiscalYear = computed(() => this.selectedFiscalYearItem()?.itemName ?? "");
    readonly selectedFiscalYearId = computed(() => this.selectedFiscalYearItem()?.itemId ?? null);

    // Summary Data
    fiscalYearSummary: FiscalYearSummary | null = null;

    private destroy$ = new Subject<void>();

    constructor(private cd: ChangeDetectorRef) {}

    ngOnInit(): void {
        this.initializeComponent();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    @Bind
    private async initializeComponent(): Promise<void> {
        // Cancel any ongoing requests
        this.destroy$.next();

        // Reset component state
        this.init.set(false);
        this.isLoading.set(true);
        this.error = null;
        this.fiscalYearSummary = null;

        this.cd.detectChanges();

        // Ensure security store is initialized
        await this.securityStore.ensureInitialized();

        const [fiscalYears, fiscalYearsError] = await S25Util.Maybe(BudgetService.getFiscalYears());
        if (fiscalYears?.length) {
            this.hasFiscalYears.set(true);
            const defaultFiscalYear = fiscalYears.find((fiscalYear) => fiscalYear.isCurrent) ?? fiscalYears[0];
            this.selectedFiscalYearItem.set({
                itemId: defaultFiscalYear.fiscalYearId,
                itemName: defaultFiscalYear.name,
                itemTypeId: Budget.BUDGET_ITEM_TYPE_ID,
            });
        } else if (fiscalYearsError) {
            console.error("Failed to load fiscal years:", fiscalYearsError);
        }

        const [_, error] = await S25Util.Maybe(this.loadAdminData());

        if (error) {
            this.error = "Failed to load budget administration data. Please try again.";
        }

        this.init.set(true);
        this.isLoading.set(false);
        this.cd.detectChanges();
    }

    @Bind
    async loadAdminData(): Promise<void> {
        this.isLoading.set(true);
        this.error = null;
        this.cd.detectChanges();

        try {
            await this.loadFiscalYearSummary();
        } catch (error) {
            this.error = error instanceof Error ? error.message : "An unexpected error occurred";
            throw error;
        } finally {
            this.isLoading.set(false);
            this.cd.detectChanges();
        }
    }

    private async loadFiscalYearSummary(): Promise<void> {
        const [summaryData, error] = await S25Util.Maybe(
            BudgetService.getAdminBudgetSummary(this.selectedFiscalYear() || ""),
        );

        if (error) {
            console.warn("Failed to load fiscal year summary:", error);
            this.fiscalYearSummary = null;
        } else {
            this.fiscalYearSummary = summaryData;
        }
    }

    onFiscalYearChange(): void {
        this.loadAdminData();
    }

    @Bind
    async exportFiscalYearBudgets(): Promise<void> {
        if (this.exporting() || !this.selectedFiscalYear() || !this.securityStore.hasFLSOverride()) {
            return;
        }
        this.exporting.set(true);
        this.cd.detectChanges();
        try {
            await OrganizationExportService.openAndExport({
                mode: "bulkByFiscalYear",
                fiscalYear: this.selectedFiscalYear(),
                fiscalYearId: this.selectedFiscalYearId() ?? undefined,
            });
        } finally {
            this.exporting.set(false);
            this.cd.detectChanges();
        }
    }

    async handleBudgetChanged(): Promise<void> {
        this.successMessage.set(BudgetLabelPhrases.allocationCreatedSuccessfully);
        await this.loadAdminData();
    }
}
